import { UserGroupIcon } from "@heroicons/react/24/outline";
import { Encounter } from "../../shared/types";
import DocumentsModal from "../modals/DocumentsModal";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useVerification } from "src/contexts/VerificationContext";
import { apiGet, apiPost } from '../../utils/api';
import BlurredCard from "../BlurredCard";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import { Patient } from "src/shared/types";
import { ToastType, useToast } from "../Toast";
import UploadDocumentModal from "../modals/UploadDocumentModal";
import ActionsMenu from "../ActionsMenu";
import TableDateTime from "../table/TableDateTime";
import TableBadge from "../table/TableBadge";
import PractitionerLink from "../table/PractitionerLink";
import UploadClinicalNoteModal from "../modals/UploadClinicalNoteModal";

const statusColorMap: Record<string, string> = {
  finished: "bg-emerald-500",
  "in-progress": "bg-amber-500",
  planned: "bg-sky-500",
  cancelled: "bg-rose-500",
};

type DocumentTypeOption = { txa_id: string; label: string };

const uploadDocumentToEncounter = async (
  encounter: Encounter,
  patientData: Patient,
  patientId: string,
  organizationId: number,
  showToast: (message: string, type: ToastType) => void,
  documentType: string,
  documentContent: string,
  documentFormat: string,
) => {
  try {
    const response = await apiPost(`/api/${organizationId}/upload-document/${patientId}`, {
      csn: encounter.csn,
      identifier: patientData.primaryIdentifier,
      lastName: patientData.lastName,
      givenNames: patientData.givenNames,
      dob: patientData.dateOfBirth,
      legalSex: patientData.gender,
      documentType,
      documentContent,
      documentFormat,
    });

    if (response.ok) {
      showToast('Document uploaded successfully', 'success');
    } else {
      showToast('Failed to upload document', 'error');
    }
  } catch (error) {
    showToast('Failed to upload document', 'error');
  }
};

const uploadClinicalNoteToEncounter = async (
  encounter: Encounter,
  patientData: Patient,
  patientId: string,
  organizationId: number,
  showToast: (message: string, type: ToastType) => void,
  noteContent: string,
  documentType: string,
) => {
  try {
    const response = await apiPost(`/api/${organizationId}/upload-clinical-note/${patientId}`, {
      csn: encounter.csn,
      identifier: patientData.primaryIdentifier,
      lastName: patientData.lastName,
      givenNames: patientData.givenNames,
      dob: patientData.dateOfBirth,
      transcriptionType: documentType,
      practitioner: encounter.practitioner,
      noteContent,
    });

    if (response.ok) {
      showToast('Clinical note uploaded successfully', 'success');
    } else {
      showToast('Failed to upload clinical note', 'error');
    }
  } catch (error) {
    showToast('Failed to upload clinical note', 'error');
  }
};

const encounterColumns: StaticDataColumnProps[] = [
  { title: "Type", sortable: true },
  { title: "Date", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Practitioner", sortable: true },
  { title: "Status", sortable: true },
  { title: "Reason", sortable: false },
];

const convertEncountersToRows = (
  encounters: Encounter[],
  isVerified: boolean,
  onViewDocuments: (encounter: Encounter) => void,
  onUploadDocument: (encounter: Encounter) => void,
  onShowClinicalNotes: (encounter: Encounter) => void,
  onShowProcedures: (encounter: Encounter) => void,
  onUploadClinicalNote: (encounter: Encounter) => void,
  documentTypeOptions: DocumentTypeOption[],
): StaticDataRow[][] => {
  return encounters.map((encounter) => [
    {
      name: "Type",
      value: encounter.type,
      element: <span className="text-gray-900">{encounter.type}</span>
    },
    {
      name: "Date",
      value: encounter.date ? new Date(encounter.date) : "—",
      element: <TableDateTime date={encounter.date} />
    },
    {
      name: "Practitioner",
      value: encounter.practitioner?.name || "—",
      element: (
        <PractitionerLink
          name={encounter.practitioner?.name || "—"}
          practitionerId={encounter.practitioner?.practitioner_id}
        />
      )
    },
    {
      name: "Status",
      value: capitalizeStatus(encounter.status),
      element: (
        <TableBadge
          text={capitalizeStatus(encounter.status)}
          bgColorClass={statusColorMap[encounter.status] ?? "bg-gray-500"}
        />
      )
    },
    {
      name: "Reason",
      value: encounter.reasonCode || "—",
      element: <span className="text-gray-600 text-sm">{encounter.reasonCode || "—"}</span>
    },
    {
      name: "Actions",
      value: "",
      element: (
        <ActionsMenu
          actions={[
            {
              id: "show-documents",
              label: "Show Documents",
              onClick: () => onViewDocuments(encounter),
              disabled: !isVerified,
            },
            {
              id: "upload-document",
              label: "Upload Document",
              onClick: () => onUploadDocument(encounter),
              disabled: !isVerified || documentTypeOptions.length === 0,
            },
            {
              id: "show-clinical-notes",
              label: "Show Clinical Notes",
              onClick: () => onShowClinicalNotes(encounter),
              disabled: !isVerified,
            },
            {
              id: "upload-clinical-note",
              label: "Upload Clinical Note",
              onClick: () => onUploadClinicalNote(encounter),
              disabled: !isVerified,
            },
            {
              id: "show-procedures",
              label: "Show Procedures",
              onClick: () => onShowProcedures(encounter),
              disabled: !isVerified,
            },
          ]}
        />
      ),
    },
  ]);
};

const dummyEncounters: Encounter[] = [
  {
    id: "1",
    type: "Encounter 1",
    date: new Date().toISOString().split('T')[0],
    status: "finished",
    practitioner: {
      practitioner_id: "123",
      name: "Dr. John Doe",
    },
    reasonCode: "Reason 1",
    csn: "1234567890",
  },
  {
    id: "2",
    type: "Encounter 2",
    date: new Date().toISOString().split('T')[0],
    status: "in-progress",
    practitioner: {
      practitioner_id: "456",
      name: "Dr. Jane Doe",
    },
    reasonCode: "Reason 2",
    csn: "1234567890",
  },
];

type EncountersCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
  onShowClinicalNotes: (encounter: Encounter) => void;
  onShowProcedures: (encounter: Encounter) => void;
};

const EncountersCard = ({ yearFrom, yearTo, refreshKey, onShowClinicalNotes, onShowProcedures }: EncountersCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId, patientData } = useOrganizationContext();
  const { isVerified } = useVerification();
  const { Toast, showToast } = useToast();

  const [ documentTypeOptions, setDocumentTypeOptions ] = useState<DocumentTypeOption[]>([]);
  const [ encounters, setEncounters ] = useState<Encounter[]>([]);
  const [ showEncounterModal, setShowEncounterModal ] = useState<Encounter | null>(null);
  const [ encounterForDocumentUpload, setEncounterForDocumentUpload ] = useState<Encounter | null>(null);
  const [ encounterForClinicalNoteUpload, setEncounterForClinicalNoteUpload ] = useState<Encounter | null>(null);
  const [ uploadSubmitting, setUploadSubmitting ] = useState(false);
  const [ loading, setLoading ] = useState(false);

  const showLoading = isVerified && loading;

  useEffect(() => {
    const fetchDocumentTypeOptions = async () => {
      try {
        const response = await apiGet(`/api/organizations/${organization.id}/document-type-options?category=document`);
        const data = await response.json();
        if (data.options) {
          setDocumentTypeOptions(data.options);
        }
      } catch (_) {}
    };

    fetchDocumentTypeOptions();
  }, [ organization.id ]);

  const uploadDocumentOnSubmit = useCallback(async (documentType: string, documentContent: string, documentFormat: string) => {
    if (!encounterForDocumentUpload || !patientData || !patientId) return;
    setUploadSubmitting(true);
    await uploadDocumentToEncounter(encounterForDocumentUpload, patientData, patientId, organization.id, showToast, documentType, documentContent, documentFormat);
    setUploadSubmitting(false);
    setEncounterForDocumentUpload(null);
  }, [ encounterForDocumentUpload, patientData, patientId, organization?.id, showToast ]);

  const uploadClinicalNoteOnSubmit = useCallback(async (content: string, documentType: string) => {
    if (!encounterForClinicalNoteUpload || !patientData || !patientId) return;
    setUploadSubmitting(true);
    await uploadClinicalNoteToEncounter(encounterForClinicalNoteUpload, patientData, patientId, organization.id, showToast, content, documentType);
    setUploadSubmitting(false);
    setEncounterForClinicalNoteUpload(null);
  }, [ encounterForClinicalNoteUpload, patientData, patientId, organization?.id, showToast ]);

  const fetchEncounters = useCallback(async () => {
    if (!organization || !patientId) {
      setEncounters([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/encounters`, params);
      const data = await response.json();

      if (data.error) {
        setEncounters([]);
        window.location.href = "/error";
      }

      setEncounters(data.encounters);
    } catch (_) {
      setEncounters([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchEncounters();
    } else {
      setEncounters(dummyEncounters);
    }
  }, [ isVerified, fetchEncounters, yearFrom, yearTo, refreshKey ]);

  const tableRows = useMemo(() =>
    convertEncountersToRows(
      encounters,
      isVerified,
      setShowEncounterModal,
      setEncounterForDocumentUpload,
      onShowClinicalNotes,
      onShowProcedures,
      setEncounterForClinicalNoteUpload,
      documentTypeOptions
    ),
    [ encounters, isVerified, onShowClinicalNotes, onShowProcedures, documentTypeOptions ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Encounters..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {encounters.length > 0 ? (
            <StaticDataTable
              tableId="encounters-table"
              columns={encounterColumns}
              rows={tableRows}
              updateUrlParams={false}
              trailingColumnKey="Actions"
              rowId={(item) => `${item.Type}-${item.Date}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <UserGroupIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No encounters found</p>
            </div>
          )}
        </div>
      )}
      <Toast />
      {showEncounterModal && <DocumentsModal encounter={showEncounterModal} onClose={() => setShowEncounterModal(null)} />}
      {encounterForDocumentUpload && (
        <UploadDocumentModal
          documentTypeOptions={documentTypeOptions}
          onClose={() => setEncounterForDocumentUpload(null)}
          onSubmit={uploadDocumentOnSubmit}
          submitting={uploadSubmitting}
        />
      )}
      {encounterForClinicalNoteUpload && (
        <UploadClinicalNoteModal
          onClose={() => setEncounterForClinicalNoteUpload(null)}
          onSubmit={uploadClinicalNoteOnSubmit}
          submitting={uploadSubmitting}
        />
      )}
    </BlurredCard>
  );
};

export default EncountersCard;
